// Display formatting only. No scoring, no business math — that is the API's job (ADR 0003 §4).

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

/** A model cost in dollars: cents when it is dollars, four places when it is cents (a turn costs $0.04). */
export function formatCost(value: number): string {
  const places = value !== 0 && Math.abs(value) < 1 ? 4 : 2
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: places }).format(value)
}

export function formatMoney(value: number): string {
  return money.format(value)
}

export function formatCompact(value: number): string {
  return compact.format(value)
}

/** 0–100 score to a whole number; anything outside the range is clamped, not hidden. */
export function formatScore(value: number): string {
  return String(Math.round(Math.min(100, Math.max(0, value))))
}

export function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/** A date as distance from now: "just now", "3h ago", "2d ago", then the short date once it is over a month old. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const seconds = Math.round((now.getTime() - d.getTime()) / 1000)
  if (seconds < 0) return formatDate(iso)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 31) return days === 1 ? 'yesterday' : `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' })
}

/** Up to two initials from a name: "Ana Ruiz-Lopez" → "AR"; an email or a single word gives one letter. */
export function initials(name: string): string {
  const words = name.replace(/[(@].*$/, '').trim().split(/[\s,.-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  return (words.length === 1 ? words[0][0] : words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Rows from a QueryResult as objects keyed by column, for tables that read like records. */
export function rowsToRecords<T extends Record<string, unknown>>(columns: string[], rows: unknown[][]): T[] {
  return rows.map((r) => Object.fromEntries(columns.map((c, i) => [c, r[i]])) as T)
}
